import React, { useState, useEffect } from "react";
import { useI18n } from "../../i18n";
import api from "../../api";

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_TYPES = ["hard_training", "light_training", "rest", "match", "double_session"];

const DailyPlan = ({ profile }) => {
  const { t, lang } = useI18n();
  const isLifestyle = profile?.profile_type === "lifestyle" || profile?.sport === "lifestyle";
  const [dayKey, setDayKey] = useState("monday");
  const [dayType, setDayType] = useState(isLifestyle ? "standard" : "hard_training");
  const [data, setData] = useState(null);
  const [soups, setSoups] = useState([]);
  const [openMeal, setOpenMeal] = useState(null);
  const [swaps, setSwaps] = useState({});
  const [mealsMode, setMealsMode] = useState("4");
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    api.post("/plan/daily", profile, {
      params: { day_type: dayType, day_key: dayKey },
    }).then((r) => setData(r.data));
  }, [profile, dayKey, dayType]);

  useEffect(() => {
    api.get("/catalog/soups").then((r) => setSoups(r.data.soups));
  }, []);

  const loadSwaps = async (foodKey, refGrams) => {
    if (swaps[foodKey]) return;
    const r = await api.get(`/plan/swaps/${foodKey}?reference_grams=${Math.round(refGrams || 100)}`);
    setSwaps((prev) => ({ ...prev, [foodKey]: r.data.swaps }));
  };

  if (!data) return <div className="text-white/60">{t.common.loading}</div>;

  // Compress N → 3 meals client-side WITHOUT dropping any kcal.
  // Backend may emit: breakfast, snack1, lunch, preworkout, snack2, dinner, evening_snack, snack3
  // Mapping (every possible meal MUST end up somewhere — total kcal MUST match the un-compressed plan):
  //   Breakfast ← breakfast + snack1
  //   Lunch     ← lunch + preworkout + snack2 (afternoon snack)
  //   Dinner    ← dinner + evening_snack + snack3 (late/top-up snack)
  const compressMeals = (meals) => {
    const find = (id) => meals.find((m) => m.id === id);
    const merge = (id, title_en, title_ro, time, sources) => {
      const foods = []; const totals = { kcal: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sodium: 0 };
      let soupSwap = false;
      sources.forEach((sid) => {
        const m = find(sid);
        if (!m) return;
        foods.push(...m.foods);
        Object.keys(totals).forEach((k) => { totals[k] += m.totals[k] || 0; });
        if (m.soup_swap_available) soupSwap = true;
      });
      Object.keys(totals).forEach((k) => totals[k] = Math.round(totals[k] * 10) / 10);
      return { id, title_en, title_ro, time, foods, totals, soup_swap_available: soupSwap };
    };
    const compressed = [
      merge("breakfast", "Breakfast", "Mic Dejun", "08:00", ["breakfast", "snack1"]),
      merge("lunch",     "Lunch",     "Prânz",    "13:30", ["lunch", "preworkout", "snack2"]),
      merge("dinner",    "Dinner",    "Cină",     "20:00", ["dinner", "evening_snack", "snack3"]),
    ];
    // Safety net: if backend ever introduces a new meal id we don't know about,
    // dump it into Dinner so kcal NEVER silently disappear.
    const known = new Set(["breakfast", "snack1", "lunch", "preworkout", "snack2", "dinner", "evening_snack", "snack3"]);
    const unknown = meals.filter((m) => !known.has(m.id));
    if (unknown.length > 0) {
      const dinner = compressed[2];
      unknown.forEach((m) => {
        dinner.foods.push(...m.foods);
        Object.keys(dinner.totals).forEach((k) => {
          dinner.totals[k] = Math.round(((dinner.totals[k] || 0) + (m.totals[k] || 0)) * 10) / 10;
        });
      });
    }
    return compressed;
  };
  const displayMeals = mealsMode === "3" ? compressMeals(data.plan.meals) : data.plan.meals;

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <label className="sne-label">Day</label>
          <select className="sne-select" data-testid="daily-day-select" value={dayKey} onChange={(e) => setDayKey(e.target.value)}>
            {DAY_KEYS.map((d) => <option key={d} value={d}>{t.plan.days[d]}</option>)}
          </select>
        </div>
        {!isLifestyle && (
          <div className="flex-1">
            <label className="sne-label">Day Type</label>
            <select className="sne-select" data-testid="daily-day-type" value={dayType} onChange={(e) => setDayType(e.target.value)}>
              {DAY_TYPES.map((d) => <option key={d} value={d}>{t.plan.day_types[d]}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-6">
        <Stat
          label={t.dashboard.tdee}
          value={`${Math.round(data.plan.totals?.kcal ?? data.plan.target.kcal)} kcal`}
          sub={`${lang === "ro" ? "țintă" : "target"} ${data.plan.target.kcal}`}
        />
        <Stat
          label="P"
          value={`${Math.round(data.plan.totals?.protein ?? data.plan.target.protein_g)} g`}
          color="#FF3B30"
          sub={`${lang === "ro" ? "țintă" : "target"} ${data.plan.target.protein_g}g`}
        />
        <Stat
          label="C"
          value={`${Math.round(data.plan.totals?.carbs ?? data.plan.target.carbs_g)} g`}
          color="#007AFF"
          sub={`${lang === "ro" ? "țintă" : "target"} ${data.plan.target.carbs_g}g`}
        />
        <Stat
          label="F"
          value={`${Math.round(data.plan.totals?.fat ?? data.plan.target.fat_g)} g`}
          color="#F59E0B"
          sub={`${lang === "ro" ? "țintă" : "target"} ${data.plan.target.fat_g}g`}
        />
      </div>

      {/* Meal structure toggle + tip */}
      <div className="sne-card mb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="font-mono text-xs tracking-widest text-white/50 mb-1">{t.plan.meals_mode}</div>
            <div className="flex gap-2">
              {["4", "3"].map((m) => (
                <button
                  key={m}
                  data-testid={`meals-mode-${m}`}
                  onClick={() => setMealsMode(m)}
                  className="font-mono text-xs tracking-widest px-3 py-2"
                  style={{
                    background: mealsMode === m ? "#FF3B30" : "transparent",
                    color: "white",
                    border: `1px solid ${mealsMode === m ? "#FF3B30" : "#27272A"}`,
                  }}
                >
                  {m === "4" ? t.plan.meals_5 : t.plan.meals_3}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => setShowTip((v) => !v)}
            data-testid="meals-tip-toggle"
            className="font-mono text-[10px] tracking-widest text-[#FF6B61] hover:text-white"
            style={{ background: "transparent", border: 0, cursor: "pointer" }}
          >
            ℹ {showTip ? (lang === "ro" ? "ascunde sfat" : "hide tip") : (lang === "ro" ? "vezi sfat" : "see tip")}
          </button>
        </div>
        {showTip && (
          <div className="mt-3 p-3 text-sm text-white/80" style={{ background: "#181111", borderLeft: "2px solid #FF3B30" }} data-testid="meals-tip-body">
            <div className="font-mono text-[10px] tracking-widest text-[#FF3B30] mb-1">ℹ {t.plan.meals_tip_title}</div>
            {t.plan.meals_tip_body}
          </div>
        )}
        {/* Dynamic structure description — honest about meal count */}
        {(() => {
          const ids = displayMeals.map(m => m.id);
          const mainCount = ids.filter(id => ["breakfast","lunch","dinner"].includes(id)).length;
          const snackCount = ids.length - mainCount;
          return (
            <div className="mt-3 font-mono text-[11px] tracking-wider text-white/55" data-testid="meal-structure">
              {t.plan.structure_label} {mainCount} {t.plan.main_meals}
              {snackCount > 0 ? ` + ${snackCount} ${t.plan.snacks_word}` : ""}
            </div>
          );
        })()}
      </div>

      <div className="space-y-3">
        {displayMeals.map((m) => (
          <div key={m.id} className="sne-card" data-testid={`meal-${m.id}`}>
            <button
              onClick={() => setOpenMeal(openMeal === m.id ? null : m.id)}
              className="w-full text-left flex items-center justify-between"
              style={{ background: "transparent", border: 0, padding: 0 }}
            >
              <div>
                <div className="font-mono text-xs tracking-widest text-white/50">{m.time}</div>
                <div className="font-heading text-2xl">{m[`title_${lang}`] || m.title_en}</div>
              </div>
              <div className="text-right">
                <div className="stat-num text-3xl">{Math.round(m.totals.kcal)}</div>
                <div className="font-mono text-xs text-white/50">kcal</div>
              </div>
            </button>
            {openMeal === m.id && (
              <div className="mt-5 pt-5" style={{ borderTop: "1px solid #27272A" }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left font-mono text-xs tracking-widest text-white/40">
                      <th className="py-2">FOOD</th>
                      <th className="text-right">G</th>
                      <th className="text-right">KCAL</th>
                      <th className="text-right" style={{ color: "#FF3B30" }}>P</th>
                      <th className="text-right" style={{ color: "#007AFF" }}>C</th>
                      <th className="text-right" style={{ color: "#F59E0B" }}>F</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.foods.map((f, i) => (
                      <React.Fragment key={i}>
                        <tr style={{ borderTop: "1px solid #1F1F1F" }}>
                          <td className="py-2">
                            <button onClick={() => loadSwaps(f.key, f.grams)}
                                    className="text-left text-white hover:text-[#FF3B30] font-mono text-sm"
                                    data-testid={`food-${m.id}-${i}`}
                                    style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}>
                              {f[`name_${lang}`] || f.name_en}
                              {f[`display_${lang}`] && (
                                <span className="ml-2 text-amber-400 font-mono text-[11px]">· {f[`display_${lang}`].split(' (')[0]}</span>
                              )}
                              <div className="font-mono text-[10px] tracking-widest text-white/30 mt-1">
                                {t.plan.swap.toUpperCase()} ↓
                              </div>
                            </button>
                            {swaps[f.key] && swaps[f.key].length > 0 && (
                              <div className="mt-2 space-y-1" data-testid={`swaps-${f.key}`}>
                                {swaps[f.key].map((s) => {
                                  const householdLabel = s[`display_${lang}`];
                                  const portionDisplay = householdLabel
                                    ? `${householdLabel} (${s.equivalent_grams}g)`
                                    : `${s.equivalent_grams}g`;
                                  return (
                                    <div key={s.key}
                                         className="font-mono text-[11px] tracking-wider text-white/70 px-2 py-1"
                                         style={{ border: "1px solid #27272A", background: "#0E0E10" }}>
                                      <span className="text-amber-400">{lang === "ro" ? "Echivalent caloric:" : "Calorie equivalent:"}</span>{" "}
                                      <span className="text-white">{portionDisplay}</span>{" "}
                                      {s[`label_${lang}`] || s.label_en}
                                      <span className="text-white/40 ml-2">≈ {s.kcal_equiv} kcal</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                          <td className="text-right font-mono text-sm">{Math.round(f.grams)}</td>
                          <td className="text-right font-mono text-sm">{Math.round(f.kcal)}</td>
                          <td className="text-right font-mono text-sm">{Math.round(f.protein)}</td>
                          <td className="text-right font-mono text-sm">{Math.round(f.carbs)}</td>
                          <td className="text-right font-mono text-sm">{Math.round(f.fat)}</td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {m.soup_swap_available && soups.length > 0 && (
                  <div className="mt-4 p-3 border border-dashed" style={{ borderColor: "#27272A" }}>
                    <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-2">
                      {t.plan.soup_swap}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {soups.map((s) => (
                        <span key={s.key} className="text-xs px-3 py-2 border border-[#27272A] text-white/80" data-testid={`soup-${s.key}`}>
                          {s[`label_${lang}`] || s.label_en} · {s.kcal} kcal
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Coach note — only shown when kcal delta exceeds ±50 from target.
          Transforms a "bug" perception into athlete freedom: "eat what you
          like until you hit the budget — your call". */}
      {data.plan?.coach_note?.type && data.plan.coach_note.type !== "balanced" && (
        <div
          data-testid="coach-note"
          className="mt-6 p-4"
          style={{
            background: data.plan.coach_note.type.includes("up") ? "#1A1410" : "#0E1418",
            border: `1px solid ${data.plan.coach_note.type.includes("up") ? "#4A3520" : "#1E3548"}`,
            borderLeft: `3px solid ${data.plan.coach_note.type.includes("up") ? "#F59E0B" : "#007AFF"}`,
            color: data.plan.coach_note.type.includes("up") ? "#FFE0B5" : "#D5E5FF",
          }}
        >
          <div className="font-mono text-[10px] tracking-widest mb-2"
               style={{ color: data.plan.coach_note.type.includes("up") ? "#F59E0B" : "#007AFF" }}>
            {lang === "ro" ? "NOTĂ COACH" : "COACH NOTE"} · {data.plan.coach_note.delta_kcal > 0 ? "+" : ""}{data.plan.coach_note.delta_kcal} kcal
          </div>
          <div className="font-heading text-lg mb-2">
            {lang === "ro" ? data.plan.coach_note.title_ro : data.plan.coach_note.title_en}
          </div>
          <div className="text-sm leading-relaxed">
            {lang === "ro" ? data.plan.coach_note.body_ro : data.plan.coach_note.body_en}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color, sub }) => (
  <div className="sne-card">
    <div className="font-mono text-xs tracking-widest text-white/50">{label}</div>
    <div className="stat-num text-3xl mt-1" style={color ? { color } : {}}>{value}</div>
    {sub && <div className="font-mono text-[10px] tracking-wider text-white/35 mt-1">{sub}</div>}
  </div>
);

export default DailyPlan;
